/**
 * Campaign Copilot API Service
 * Handles communication with the campaign-copilot backend endpoints
 */

import http from './http';

// Types
export interface CampaignCopilotMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  metadata?: {
    fieldFocus?: string;
    toolCalls?: string[];
    campaignUpdates?: {
      field: string;
      oldValue?: string;
      newValue: string;
    }[];
  };
}

export interface CampaignCopilotState {
  currentFieldFocus?: string;
  lastModifiedFields: string[];
  campaignSnapshot?: {
    title?: string;
    description?: string;
    destination?: string;
  };
}

export interface ConversationTabInfo {
  conversationId: string;
  title: string;
  lastMessageAt: string;
  isActive: boolean;
}

export interface CampaignCopilotConversation {
  conversationId: string;
  title: string;
  messages: CampaignCopilotMessage[];
  state: CampaignCopilotState;
}

export interface CampaignCopilotChatResponse {
  message: string;
  conversationId: string;
  state: CampaignCopilotState;
  campaignUpdates?: {
    field: string;
    value: string;
  }[];
}

// SSE Event types
export type CampaignCopilotSSEEventType =
  | 'text'
  | 'complete'
  | 'campaign_updated'
  | 'ui_action'
  | 'error';

// UI Action types
export type CampaignCopilotUIActionType =
  | 'open_product_modal'
  | 'show_catalogs'
  | 'select_catalog'
  | 'open_customer_profile_modal'
  | 'open_location_modal'
  | 'open_service_area_modal';

// Customer Profile prefill data
export interface CustomerProfilePrefill {
  _id?: string; // MongoDB ID if profile is already saved
  name?: string;
  description?: string;
  demographics?: {
    ageRange?: string;
    gender?: string;
    income?: string;
  };
  interests?: string[];
  painPoints?: string[];
  customer_rules?: any[];
  customer_tones?: any[];
  customer_keywords?: string[];
}

// Location prefill data
export interface LocationPrefill {
  name?: string;
  address?: string;
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  lat?: number;
  lng?: number;
  phone_number?: string;
}

// Service Area prefill data
export interface ServiceAreaPrefill {
  name?: string;
  type?: 'radius' | 'geographic';
  address?: string;
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  lat?: number;
  lng?: number;
  radius?: number;
}

export interface CampaignCopilotUIAction {
  action: CampaignCopilotUIActionType;
  data?: {
    prefill_name?: string;
    prefill_description?: string;
    prefill_type?: 'product' | 'service';
    prefill_price?: number;
    catalogId?: string;
    // Customer profile prefill
    customerProfile?: CustomerProfilePrefill;
    // Location prefill
    location?: LocationPrefill;
    // Service area prefill
    serviceArea?: ServiceAreaPrefill;
  };
}

export interface CampaignCopilotSSEEvent {
  type: CampaignCopilotSSEEventType;
  content?: string;
  message?: string;
  conversationId?: string;
  state?: CampaignCopilotState;
  field?: string;
  value?: string;
  campaignUpdates?: {
    field: string;
    value: string;
  }[];
  // UI Action fields
  action?: CampaignCopilotUIActionType;
  data?: CampaignCopilotUIAction['data'];
}

/**
 * List all conversations for a campaign
 */
export async function listConversations(
  campaignId: string
): Promise<ConversationTabInfo[]> {
  const response = await http.get<{
    success: boolean;
    data: ConversationTabInfo[];
  }>(`/v1/campaign-copilot/conversations/${campaignId}`);

  if (response.data.success) {
    return response.data.data;
  }
  throw new Error('Failed to list conversations');
}

/**
 * Create a new conversation for a campaign
 */
export async function createConversation(
  campaignId: string,
  fieldFocus?: string
): Promise<CampaignCopilotConversation> {
  const response = await http.post<{
    success: boolean;
    data: CampaignCopilotConversation;
    message?: string;
  }>(`/v1/campaign-copilot/conversations/${campaignId}`, {
    fieldFocus,
  });

  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Failed to create conversation');
}

/**
 * Delete a conversation
 */
export async function deleteConversation(
  conversationId: string
): Promise<void> {
  const response = await http.delete<{
    success: boolean;
    message?: string;
  }>(`/v1/campaign-copilot/conversations/${conversationId}`);

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to delete conversation');
  }
}

/**
 * Rename a conversation
 */
export async function renameConversation(
  conversationId: string,
  title: string
): Promise<{ conversationId: string; title: string }> {
  const response = await http.patch<{
    success: boolean;
    data: { conversationId: string; title: string };
    message?: string;
  }>(`/v1/campaign-copilot/conversations/${conversationId}/rename`, {
    title,
  });

  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Failed to rename conversation');
}

/**
 * Get conversation history
 */
export async function getConversationHistory(
  conversationId: string
): Promise<{
  messages: CampaignCopilotMessage[];
  state: CampaignCopilotState;
}> {
  const response = await http.get<{
    success: boolean;
    data: {
      messages: CampaignCopilotMessage[];
      state: CampaignCopilotState;
    };
  }>(`/v1/campaign-copilot/chat/${conversationId}/history`);

  if (response.data.success) {
    return response.data.data;
  }
  throw new Error('Failed to get conversation history');
}

/**
 * Send a message (non-streaming)
 */
export async function sendMessage(
  conversationId: string,
  campaignId: string,
  message: string
): Promise<CampaignCopilotChatResponse> {
  const response = await http.post<{
    success: boolean;
    data: CampaignCopilotChatResponse;
    message?: string;
  }>('/v1/campaign-copilot/chat', {
    conversationId,
    campaignId,
    message,
  });

  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Failed to send message');
}

/**
 * Get or create the single conversation for a campaign
 * Returns existing conversation or creates a new one
 */
export async function getOrCreateConversation(
  campaignId: string,
  fieldFocus?: string
): Promise<CampaignCopilotConversation> {
  const response = await http.post<{
    success: boolean;
    data: CampaignCopilotConversation & { isNew: boolean };
    message?: string;
  }>(`/v1/campaign-copilot/conversations/${campaignId}/get-or-create`, {
    fieldFocus,
  });

  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Failed to get or create conversation');
}

/**
 * Send a message with streaming response
 * Returns an EventSource-like interface for SSE
 */
export function sendMessageStream(
  conversationId: string,
  campaignId: string,
  message: string,
  onEvent: (event: CampaignCopilotSSEEvent) => void,
  onError: (error: Error) => void,
  onComplete: () => void
): AbortController {
  const abortController = new AbortController();

  // Get auth token
  const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
  const accessToken = tokens?.access?.token;
  const accountId = localStorage.getItem('accountId');

  if (!accessToken) {
    onError(new Error('Not authenticated'));
    onComplete();
    return abortController;
  }

  // Use fetch with streaming - remove /v1 since apiUrl already includes it
  const apiUrl = (import.meta.env.VITE_API_URL || '').replace(/\/v1\/?$/, '');

  fetch(`${apiUrl}/v1/campaign-copilot/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      'x-account-id': accountId || '',
    },
    body: JSON.stringify({
      conversationId,
      campaignId,
      message,
    }),
    signal: abortController.signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          onComplete();
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              onComplete();
              return;
            }
            try {
              const event = JSON.parse(data) as CampaignCopilotSSEEvent;
              onEvent(event);
            } catch {
              // Ignore parse errors for partial data
            }
          }
        }
      }
    })
    .catch((error) => {
      if (error.name !== 'AbortError') {
        onError(error);
      }
      onComplete();
    });

  return abortController;
}

// Creative content generation types
export type CreativeFieldType =
  | 'headline'
  | 'primary_text'
  | 'description'
  | 'long_headline'
  | 'business_name'
  | 'call_to_action';

export interface GenerateCreativeContentRequest {
  campaignId: string;
  fieldName: CreativeFieldType;
  maxChars: number;
  channel?: string;
  currentValue?: string;
}

export interface GenerateCreativeContentResponse {
  content: string;
  fieldName: CreativeFieldType;
  charCount: number;
}

/**
 * Generate creative content for a campaign field using AI
 * Strictly respects character limits
 */
export async function generateCreativeContent(
  request: GenerateCreativeContentRequest
): Promise<GenerateCreativeContentResponse> {
  const response = await http.post<{
    success: boolean;
    data: GenerateCreativeContentResponse;
    message?: string;
  }>('/v1/campaign-copilot/generate-creative-content', request);

  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Failed to generate creative content');
}

export default {
  listConversations,
  createConversation,
  deleteConversation,
  renameConversation,
  getConversationHistory,
  sendMessage,
  sendMessageStream,
  getOrCreateConversation,
  generateCreativeContent,
};
